import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View } from 'react-native';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { useAsyncEffect } from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import formatProfilePhoto from '../../../../modules/formatters/formatProfilePhoto';
import type { IRootDispatch, RootState } from '../../../../store/models';
import BookButton from '../BookButton';
import { PublicProvider } from '../../../../api/Providers';
import ProfileImageSwiper from './ProfileImageSwiper';
import { GalleryImageServiceContent } from './GalleryImageServiceContent';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { ProfileGalleryImage } from '../../../../api/Providers/GalleryImages';

export type ProfileImageSwiperContainerProps = {
  galleryImages: ProfileGalleryImage[];
  profile: ClientProviderProfile | PublicProvider;
  initialIndex: number;
  isEditMode?: boolean;
  onClose: () => void;
  onBookNowPress?: (image: ProfileGalleryImage) => void;
  onAvatarPress: () => void;
  onIndexChanged?: (index: number) => void;
  onFetchNext?: () => void;
  imageTotal?: number;
  displayHeader?: boolean;
};

const LOAD_NEXT_PAGE_ZONE = 10; // load next page when ten from the end

export const ProfileImageSwiperContainer: React.FCWithChildren<ProfileImageSwiperContainerProps> = ({
  galleryImages,
  profile,
  initialIndex,
  isEditMode,
  onClose,
  onBookNowPress,
  onAvatarPress,
  onIndexChanged,
  imageTotal,
  onFetchNext,
  displayHeader = true,
}) => {
  const providerId = profile?.id;
  const selectServicesById = (state: RootState) => (
    state.profileServiceGroups[providerId]?.servicesById
  );
  const servicesById = useSelector<RootState, ReturnType<typeof selectServicesById>>(
    selectServicesById,
  );

  const dispatch = useDispatch<IRootDispatch>();
  useAsyncEffect(async () => {
    if (!servicesById) {
      dispatch.profileServiceGroups.loadProvider({ providerId });
    }
  }, [
    providerId,
    servicesById,
  ]);
  const [galleryIndex, setGalleryIndex] = useState<number>(initialIndex);

  const currentImage = galleryImages?.[galleryIndex];
  const currentService = servicesById?.[currentImage?.service_id] || null;

  const handleBookNowPress = useCallback(() => {
    analytics.track('client_profile_gallery_modal_cta_clicked', {
      provider_id: providerId,
    });
    onBookNowPress?.(galleryImages[galleryIndex]);
  }, [
    onBookNowPress,
    galleryImages,
    galleryIndex,
    providerId,
  ]);

  const handleClose = useCallback(() => {
    analytics.track('client_profile_gallery_modal_close', {
      provider_id: providerId,
    });
    onClose();
  }, [
    providerId,
    onClose,
  ]);

  const handleAvatarPress = () => {
    analytics.track('client_profile_gallery_modal_avatar_clicked', {
      provider_id: providerId,
    });
    onAvatarPress();
  };

  const handleIndexChanged = useCallback((index: number) => {
    /** Handle pagination in the swiper */
    if (index >= galleryImages.length - LOAD_NEXT_PAGE_ZONE) {
      onFetchNext?.();
    }

    setGalleryIndex(index);
    onIndexChanged?.(index);
  }, [
    onIndexChanged,
    galleryImages,
    onFetchNext,
  ]);

  const handlePricePress = () => {
    analytics.track('client_profile_gallery_modal_price_clicked', {
      provider_id: providerId,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const handleServicePress = () => {
    analytics.track('client_profile_gallery_modal_service_clicked', {
      provider_id: providerId,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const handleDurationPress = () => {
    analytics.track('client_profile_gallery_modal_duration_clicked', {
      provider_id: providerId,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const handleBlurbPress = () => {
    analytics.track('client_profile_gallery_modal_info_clicked', {
      provider_id: providerId,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const handleExpandPress = (isExpanded: boolean) => {
    analytics.track('client_profile_gallery_modal_info_expanded', {
      provider_id: providerId,
      expanded: isExpanded,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const handleImagePress = () => {
    analytics.track('client_profile_gallery_modal_image_clicked', {
      provider_id: providerId,
      image_id: currentImage?.id,
      service_id: currentService?.id,
    });
  };

  const renderBottomContent = useCallback(() => {
    const bottomContent = currentService ? (
      <Box
        padding={20}
        borderTopWidth={1}
        borderStyle="solid"
        borderTopColor={BrandColor.LightGrey}
        style={{
          alignSelf: 'center',
          width: '100%',
          maxWidth: 540,
        }}
      >
        <BookButton
          testID="imageGalleryBookButton"
          additionalTracking={{
            click_location: 'profile_image_gallery',
          }}
          isOnlineBookable={(
            profile.is_effectively_online_bookable
            && !profile.is_disabled
            && !!currentService.is_online_bookable
          )}
          service={currentService}
          providerId={providerId}
          providerPhone={profile.public_phone_number}
          isEditMode={isEditMode}
          onClick={handleBookNowPress}
        />
      </Box>
    ) : <View style={{ height: 86 }} />; // preserve the space it takes up
    return bottomContent;
  }, [
    currentService,
    handleBookNowPress,
    providerId,
    profile,
    isEditMode,
  ]);

  return (
    <ProfileImageSwiper
      provider={profile}
      onCloseRequest={handleClose}
      providerProfilePhoto={profile?.profile_photo ? formatProfilePhoto(profile.profile_photo, '_100x100.jpg') : null}
      formatImageUrl={img => formatProfilePhoto(img.url, '_720x540.jpg')}
      images={galleryImages}
      initialIndex={initialIndex}
      onIndexChanged={handleIndexChanged}
      onAvatarPress={handleAvatarPress}
      onImagePress={handleImagePress}
      renderBottomContent={renderBottomContent}
      imageTotal={imageTotal}
      displayHeader={displayHeader}
    >
      {currentService ? (
        <GalleryImageServiceContent
          testID="profile-gallery-content"
          serviceName={currentService.name}
          duration={currentService.duration_minutes}
          price={Number(currentService.cost)}
          blurb={currentService.blurb}
          onServicePress={handleServicePress}
          onDurationPress={handleDurationPress}
          onPricePress={handlePricePress}
          onBlurbPress={handleBlurbPress}
          onExpandPress={handleExpandPress}
        />
      ) : <Box flexGrow={1} alignSelf="stretch" />}
    </ProfileImageSwiper>
  );
};
